import React, {useCallback, useEffect, useMemo, useState} from "react";
import classNames from "classnames";
import {useAuthenticatedApi} from "@webng/react-app-common";
import {useTranslation} from "react-i18next";
import {WebEmbed} from "../webembed/WebEmbed";
import {OEmbed} from "../webembed/OEmbed";
import {IWebEmbedContentBlock} from "@webng-types/write-model";
import {ApiClient, AuthenticatedApiClient} from "@webng/tickaroo-api";
import {reduceEmbedCode} from "@webng/web-embed-preprocessors";
import {isRelativeUrl} from "../utils/regex";
import {Info} from "react-feather";
import {useRelativeLinkBaseUrl} from "../LiveblogRenderContext";

interface WebEmbedContentBlockViewProps {
  eventId?: string|undefined
  block: IWebEmbedContentBlock
  className?: string
  isPreview?: boolean
}

export interface UpdateOEmbedResult {
  requestedUrl: string
  error?: any
  oembed?: OEmbed
}


export function updateOEmbed(apiClient: AuthenticatedApiClient, url: string, callback: (result: UpdateOEmbedResult) => void, force: boolean = false) {
  // Reduce pasted embed codes to their canonical provider URL before the request is
  // built: raw codes (an Instagram blockquote is ~7 KB) exceed the CDN's request-URL
  // quota when they ride in the ?url= query string and never reach the service.
  // `requestedUrl` keeps reporting the original input so the consumers' current-input
  // guards still accept the result (resolve-long-embed-codes, design D6).
  const requestUrl = reduceEmbedCode(url) ?? url
  // `force` is only ever set by the editor's explicit refresh action (add-web-embed-refresh-button)
  // — never by this default, and never by the reader-render self-heal effect below. Both of
  // those must keep using the cached request; see design.md for why that fence matters.
  const query: Record<string, string> = {url: requestUrl}
  if(force) query.nocache = "1"
  return apiClient.getJson(ApiClient.API_WEB_EMBED, query).then(value => {
    callback({
      requestedUrl: url,
      oembed: value
    })
  }).catch(error => {
    callback({
      requestedUrl: url,
      error: error
    })
  })
}

export function WebEmbedContentBlockView({eventId, block, className, isPreview} : WebEmbedContentBlockViewProps) {
  const [loadedJson, setLoadedJson] = useState<OEmbed|undefined>(undefined)
  const apiClient = useAuthenticatedApi();
  const {i18n, t} = useTranslation("liveblog");
  const relativeLinkBaseUrl = useRelativeLinkBaseUrl();

  const baseUrl = relativeLinkBaseUrl || ((typeof window !== "undefined") && window.location && window.location.origin);
  const url = baseUrl && isRelativeUrl(block.url) ? new URL(block.url, baseUrl).toString() : block.url
  const showRelativeLinkHint = isRelativeUrl(block.url) && isPreview === true

  // A reduced embed code resolves with _original = the reduced URL, and this read-only
  // view cannot rewrite block.url — staleness below is compared against the effective
  // (reduced) input, or a block storing a raw code without oembed_json would refetch
  // forever (resolve-long-embed-codes, design D6/F9).
  const effectiveUrl = useMemo(() => reduceEmbedCode(url) ?? url, [url])

  const json = useMemo(() => {
    if(loadedJson) {
      return loadedJson;
    } else if(block.oembed_json) {
      return JSON.parse(block.oembed_json)
    } else {
      return undefined;
    }
  }, [block.oembed_json, loadedJson]);

  const onOEmbedUpdated = useCallback((result: UpdateOEmbedResult) => {
    if(url === result.requestedUrl) {
      if(result.oembed) {
        setLoadedJson(result.oembed)
      }
    }
  }, [url, setLoadedJson])

  useEffect(() => {
    // current when _original matches the effective (reduced) input OR the stored raw
    // input — legacy blocks resolved before reduction persisted _original = the raw
    // code, and treating those as stale would refetch on every reader mount
    // (Codex P2 on PR #789).
    const isCurrent = json && (json._original === effectiveUrl || json._original === url)
    if(!isCurrent && !showRelativeLinkHint) {
      const promise = updateOEmbed(apiClient, url, onOEmbedUpdated)
      return () => promise.cancel()
    }
  }, [json, apiClient, url, effectiveUrl, onOEmbedUpdated, showRelativeLinkHint])

  if(json) {
    return <WebEmbed eventId={eventId} blockId={block.local_id!}
                     oembed={json}
                     locale={i18n.language}
                     className={classNames(`tik4-we tik4-we--${json.type} tik4-we--${json.provider_name?.toLowerCase()}`, className)}
                     data-tik4-we-url={url} />
  } else {
    if (showRelativeLinkHint) {
      return <div className={classNames("tik4-we", className)} data-tik4-we-url={block.url}>
        <div className="tik4-hint tik4-hint--sm tik4-hint--box tik4-we__relative-link-hint tik4-break-safe">
          <Info className="tik4-icon" />
          <div className="tik4-hint__content">
            <div className="tik4-hint__hl">
              {block.url}
            </div>
            {t("liveblog.webembed.relative_link_hint")}
          </div>
        </div>
      </div>
    } else {
      // return <div className={classNames("tik4-we", className)} data-tik4-we-url={block.url}>
      //   <a href={url} target="_blank" rel="noopener noreferrer" className="tik4-link">{block.url}</a>
      // </div>
      return <div className={classNames("tik4-we", className)} data-tik4-we-url={block.url} />
    }
  }

  // return <div className={classNames("tik4-we", className)} data-tik4-we-url={block.url} />
}
